<template>
  <div class="exercise-definition-dialog-form-body">
    <v-container
      class="exercise-definition-dialog-card-container"
      v-if="additionalStep"
    >
      <v-row>
        <v-col class="text-center mt-8">
          <p>Have you created Exercises for this Circuit yet?</p>
          <v-btn
            rounded
            color="bhWhite"
            elevation="0"
            class="mr-2"
            @click="additionalStepAction('no')"
            >No</v-btn
          >
          <v-btn
            rounded
            color="accent"
            elevation="0"
            @click="additionalStepAction('yes')"
            >Yes</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-checkbox label="Don't show again" v-model="dontShowAgain" />
        </v-col>
      </v-row>
    </v-container>
    <v-tabs-items v-model="tabSeries" v-else>
      <v-tab-item class="exercise-definition-dialog__tab">
        <v-container class="exercise-definition-dialog-card-container">
          <dialog-input
            label="Title"
            name="title"
            :textarea="true"
            :value="(editedExercise && editedExercise.title) || ''"
            :readonly="readonly"
            :rules="['counter']"
            :textareaExpandable="true"
            @input="onInput"
          />
        </v-container>
        <v-container class="exercise-definition-dialog-card-container pt-0">
          <dialog-input
            label="Description"
            name="description"
            :textarea="true"
            :value="(editedExercise && editedExercise.description) || ''"
            :readonly="readonly"
            :rules="['counter']"
            :textareaExpandable="true"
            @input="onInput"
          />
        </v-container>
        <v-container v-if="repetitionOptionsEnabled">
          <div>
            <h5>Repeat</h5>
            <div class="d-flex align-center">
              <bh-number-input
                name="repetitionWeeks"
                v-model="repetitionWeeksValue"
                :value="0"
                :readonly="readonly"
                :minNumber="0"
                :maxNumber="20"
                input-class="rounded-0"
                @input="onInput({ name: 'repetitionWeeks', value: $event })"
              ></bh-number-input>
              <span class="ml-2">weeks</span>
            </div>
          </div>
          <div v-if="repetitionDaysOptions && repetitionDaysOptions.length > 0">
            <v-chip-group
              active-class="primary--text"
              v-model="repetitionDaysValue"
              :value="(editedExercise && editedExercise.repetitionDays) || []"
              multiple
              @change="onInput({ name: 'repetitionDays', value: $event })"
            >
              <v-chip
                v-for="day in repetitionDaysOptions"
                :key="day.value"
                :value="day.value"
                :readonly="readonly"
                active-class="exercise-definition-dialog__repetition-day--active"
                class="exercise-definition-dialog__repetition-day"
                name="repetitionDays"
              >
                {{ day.label }}
              </v-chip>
            </v-chip-group>
          </div>
        </v-container>
        <v-container class="exercise-definition-dialog-card-container pt-0">
          <v-row>
            <v-col sm="12" class="pt-0" v-if="!readonly">
              <thumbnail-picker
                label="Content"
                :current-file="newExerciseData.currentMedia"
                :readonly="readonlyMedia"
                @thumbnail-picked="setMedia"
              />
            </v-col>
          </v-row>
        </v-container>
        <v-container
          :class="
            `exercise-definition-dialog-card-container ${
              showAdditionalMedia ? '' : 'd-none'
            }`
          "
        >
          <v-row>
            <v-col sm="12" class="pt-0" v-if="!readonly">
              <thumbnail-picker
                label="Thumbnail"
                :current-file="newExerciseData.currentAdditionalMedia"
                :current-mobile-static-media="
                  newExerciseData.currentMobileStaticMedia
                "
                :enable-mobile-static-media="true"
                :force-mobile-static-media="true"
                :readonly="readonly"
                :allowed-file-types="[18]"
                @thumbnail-picked="setAdditionalMedia"
              />
            </v-col>
          </v-row>
        </v-container>
        <v-container
          class="exercise-definition-dialog-card-container"
          v-if="parametersEnabled"
        >
          <div>
            <h5>Rest time between exercises</h5>
            <div class="d-flex align-center">
              <bh-number-input
                name="restExercises"
                v-model="restExercisesValue"
                :value="0"
                :readonly="readonly"
                @input="
                  onInput({ name: 'restTimeAfterExercise', value: $event })
                "
              ></bh-number-input>
              <span>seconds</span>
            </div>
          </div>
        </v-container>
        <v-container
          class="exercise-definition-dialog-card-container"
          v-if="!participantsExercises"
        >
          <div>
            <h5>Repeats</h5>
            <div class="d-flex align-center">
              <bh-number-input
                name="restExercises"
                v-model="circuitsValue"
                :value="1"
                :min-number="1"
                :max-number="10"
                :readonly="readonly"
                @input="onInput({ name: 'circuits', value: $event })"
              ></bh-number-input>
              <span>x</span>
            </div>
          </div>
        </v-container>
      </v-tab-item>
      <v-tab-item class="exercise-definition-dialog__tab">
        <div class="exercise-definition-dialog__selected">
          <v-container
            v-if="selectedExerciseDefinitions.length > 0"
            class="exercise-definition-dialog-card-container"
          >
            <h5>Selected exercises</h5>
            <div class="main-user-table-wrapper">
              <table
                class="elevation-0 main-user-table"
                style="border-spacing:0 8px;"
              >
                <draggable
                  group="exercises"
                  v-model="selectedExerciseDefinitionsData"
                  tag="tbody"
                  @change="reorderSelectedExerciseDefinitions"
                >
                  <exercise-definition-selectable-table-row
                    v-for="exercise in selectedExerciseDefinitionsData"
                    :key="exercise.id"
                    :user="exercise"
                    @click.native="chooseExerciseDefinition(exercise)"
                  />
                </draggable>
                <tr class="main-user-table-empty">
                  <td class="main-user-table-empty">&nbsp;</td>
                </tr>
              </table>
            </div>
          </v-container>
        </div>
        <v-container
          class="exercise-definition-dialog-card-container"
          v-if="addToSeries"
        >
          <v-row>
            <v-col sm="12" class="pb-0">
              <v-text-field
                :value="defaultQueryParams.query"
                class="bh-input-pill rounded-pill"
                color="accent"
                height="48px"
                placeholder="Search for exercises"
                filled
                clearable
                hide-details
                rounded
                :disabled="readonly"
                @input="onSearch('query', $event)"
                @click:clear="clearSearch"
                @click:append="runSearch"
                @keydown.enter="runSearch"
              >
                <template v-slot:append>
                  <v-progress-circular
                    v-if="exerciseDefinitionsLoading"
                    size="16"
                    width="2"
                    color="info"
                    indeterminate
                  ></v-progress-circular>
                  <v-icon v-else>bhi-loupe</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12" class="">
              <main-user-table
                :headers="headers"
                :items="searchedExerciseDefinitions"
                :items-per-page="queryParams.pageSize"
                :server-items-length="
                  pagination ? pagination.totalCount || -1 : -1
                "
                :page="queryParams.page"
                :row-component="'exercise-definition-selectable-table-row'"
                @choose-exercise="chooseExerciseDefinition"
                @action:get-data="getData"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>
    </v-tabs-items>
    <v-container
      v-if="!additionalStep"
      class="d-flex justify-space-between align-center exercise-definition-dialog__tab--footer"
    >
      <v-btn
        v-if="!hidePrev"
        rounded
        color="primary"
        elevation="0"
        :disabled="tabSeries == 0 && !cancelable"
        @click="prevStepSeries"
      >
        Prev
      </v-btn>
      <span v-else></span>
      <div class="exercise-definition-dialog__dots">
        <span :class="{ active: tabSeries == 0 }"></span>
        <span :class="{ active: tabSeries == 1 }"></span>
      </div>
      <v-btn
        rounded
        color="primary"
        elevation="0"
        v-if="tabSeries != 1"
        @click="tabSeries++"
      >
        Next
      </v-btn>
      <v-btn
        rounded
        color="primary"
        elevation="0"
        :loading="submitLoading"
        @click="onSubmit(false)"
        :disabled="!formIsValidCheck"
        v-if="tabSeries == 1"
      >
        {{ editedExercise ? 'Save' : 'Create' }}
      </v-btn>
    </v-container>
    <v-dialog
      v-model="showConfirmationPopup"
      persistent
      max-width="290"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card>
        <v-card-title class="headline">Remove exercises?</v-card-title>
        <v-card-text>
          This operation is irreversible, once you remove remove Single Exercise
          from Circuit, you will not be able to add it back.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="rounded-pill" text @click="cancelRemoval">Cancel</v-btn>
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            :loading="submitLoading"
            @click="onSubmit(true)"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import DialogInput from '@/components/forms/DialogInput.vue'
import MainUserTable from '@/components/tables/MainUserTable'
import RouterMixin from '@/mixins/router'
import BhNumberInput from '@/components/inputs/BhNumberInput.vue'
import { debounce } from '@/helpers/debounce'
import ExerciseDefinitionSelectableTableRow from '@/components/exercises/ExerciseDefinitionSelectableTableRow'
import draggable from 'vuedraggable'
import TenantTextsMixin from '@/mixins/tenantTexts'
import { REPETITION_DAYS_OPTIONS } from '@/helpers/constants'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'

export default {
  name: 'SeriesExerciseDefinitionPartial',
  components: {
    DialogInput,
    MainUserTable,
    BhNumberInput,
    ExerciseDefinitionSelectableTableRow,
    draggable,
    ThumbnailPicker,
  },
  mixins: [RouterMixin, TenantTextsMixin],
  props: {
    getResource: {
      type: Function,
      default: () => null,
    },
    exerciseResource: {
      type: [Boolean, Object, null],
      default: false,
    },
    exerciseResourceAdditional: {
      type: [Boolean, Object, null],
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    day: {
      type: [Boolean, Number],
      default: false,
    },
    getMimeTypes: {
      type: Function,
      default: () => null,
    },
    exerciseDefinitions: {
      type: Array,
      default: () => [],
    },
    editedExercise: {
      type: [Boolean, Object],
      default: false,
    },
    exerciseDefinitionsLoading: {
      type: Boolean,
      default: false,
    },
    cancelable: {
      type: Boolean,
      default: false,
    },
    addToSeries: {
      type: Boolean,
      default: true,
    },
    participantsExercises: {
      type: Boolean,
      default: false,
    },
    readonlyMedia: {
      type: Boolean,
      default: false,
    },
    showAdditionalMedia: {
      type: Boolean,
      default: true,
    },
    parametersEnabled: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => {},
    },
    hidePrev: {
      type: Boolean,
      default: false,
    },
    repetitionOptionsEnabled: {
      type: Boolean,
      default: false,
    },
    waitForSubmitResponse: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabSeries: 0,
      headers: [],
      dontShowAgain: false,
      // additionalStep: true, // TODO remove
      newExerciseData: {
        title: '',
        description: '',
        externalUrl: '',
        currentMedia: null,
        media: null,
        currentAdditionalMedia: null,
        additionalMedia: null,
        mobileStaticMedia: null,
        repetitionWeeks: 0,
        repetitionDays: [],
      },
      defaultQueryParams: {
        query: '',
        pageSize: 50,
      },
      resourceName: null,
      resourceNameAdditional: null,
      formIsValidCheck: false,
      uploadFile: [],
      uploadFileThumbnail: [],
      restExercises: false,
      restExercisesValue: null,
      repetitionWeeksValue: 0,
      repetitionDaysValue: [],
      circuits: false,
      circuitsValue: null,
      searchedExerciseDefinitions: [],
      selectedExerciseDefinitions: [],
      selectedExerciseDefinitionsData: [],
      showMlFilePickerDialog: false,
      showMlFilePickerDialogType: 'media',
      showConfirmationPopup: false,
      additionalStep: true,
      showStaticMediaPicker: false,
      submitLoading: false,
    }
  },
  watch: {
    editedExercise: function(val) {
      this.submitLoading = false
      this.setupExerciseData()
    },
    exerciseDefinitions: function(val) {
      this.submitLoading = false
      this.setupExerciseData()
      this.calculateExerciseDefinitions()
    },
  },
  created() {
    this.clearSearch()
    this.submitLoading = false
    if (this.editedExercise) {
      this.additionalStep = false
    }
    if (this.getSettings?.routines_circuit_disable_tutorial)
      this.additionalStep = false
  },
  computed: {
    ...mapState('staticMediaStates', ['staticMedia']),
    ...mapGetters({
      getProfileId: 'userStates/getProfileId',
      getSettings: 'userStates/getSettings',
    }),
    user: {
      get() {
        return this.$store.state.userStates.user
      },
      set(val) {
        this.$store.commit('userStates/updateUser', val)
      },
    },
    repetitionDaysOptions() {
      if (!REPETITION_DAYS_OPTIONS) return []
      return Object.values(REPETITION_DAYS_OPTIONS).map(value => ({
        label: value.substr(0, 1),
        value,
      }))
    },
  },
  methods: {
    ...mapActions('userStates', ['getProfile', 'updateProfile']),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'
      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
    onInput({ name, value }) {
      this.newExerciseData[name] = value
      this.formIsValid()
    },
    onSubmit(force = false) {
      this.newExerciseData.type = 'SERIES'

      if (this.selectedExerciseDefinitions.length > 0) {
        this.newExerciseData.exerciseDefinitions = this.selectedExerciseDefinitions
      } else delete this.newExerciseData.exerciseDefinitions

      if (this.repetitionOptionsEnabled) {
        this.newExerciseData.repetitionWeeks = this.repetitionWeeksValue
        this.newExerciseData.repetitionDays = this.repetitionDaysValue
      }

      if (this.restExercisesValue == 0) {
        delete this.newExerciseData.restTimeAfterExercise
      } else {
        this.newExerciseData.restTimeAfterExercise = this.restExercisesValue
      }

      if (this.circuitsValue == 0) {
        delete this.newExerciseData.circuits
      } else {
        this.newExerciseData.circuits = this.circuitsValue
      }

      if (this.editedExercise) {
        let key = 'exercises'
        if (this.editedExercise.exerciseDefinitions) key = 'exerciseDefinitions'
        if (
          this.editedExercise.exercises &&
          this.selectedExerciseDefinitions.length !=
            this.editedExercise[key].length &&
          !force
        ) {
          this.showConfirmationPopup = true
          return
        }
        if (this.participantsExercises) {
          let selected = this.selectedExerciseDefinitionsData.map(item => {
            if (item.selected) return item.id
          })
          if (selected.length > 0)
            this.newExerciseData.exerciseDefinitions = selected
          else delete this.newExerciseData.exerciseDefinitions
        }

        this.submitLoading = true
        this.$emit('on-edit', { ...this.newExerciseData })
      } else {
        this.submitLoading = true
        this.$emit('on-submit', { ...this.newExerciseData })
      }
      if (!this.waitForSubmitResponse) {
        this.closeDialog()
      }
    },
    formIsValid() {
      this.formIsValidCheck = true
      if (
        !this.selectedExerciseDefinitions ||
        this.selectedExerciseDefinitions.length == 0
      )
        this.formIsValidCheck = false
      let selected = this.selectedExerciseDefinitionsData.filter(
        item => item.selected,
      )
      if (selected.length == 0) this.formIsValidCheck = false
      if (
        this.newExerciseData.title.trim() == '' ||
        this.newExerciseData.title.length > 250
      )
        this.formIsValidCheck = false
      if (this.newExerciseData.description?.length > 250)
        this.formIsValidCheck = false
    },
    closeDialog() {
      this.$emit('close-exercise-dialog')
    },
    onSearch(key, value) {
      this.defaultQueryParams.query = value
      debounce(
        () => {
          this.$emit('on-series-search', this.defaultQueryParams)
        },
        1000,
        'ExercisesSearch',
      )
    },
    clearSearch() {
      this.defaultQueryParams.query = ''
      this.$emit('on-series-search', this.defaultQueryParams)
    },
    runSearch() {
      if (this.defaultQueryParams.query)
        this.onSearch('query', this.defaultQueryParams.query)
    },
    chooseExerciseDefinition(exercise) {
      // exercise can be both object with id or integer, depends where method is fired
      let id = exercise
      let exObj = exercise
      if (typeof exercise === 'object') {
        id = exercise.id
      } else {
        exObj = this.searchedExerciseDefinitions.find(item => item.id == id)
      }
      if (!this.readonly) {
        let ind = this.selectedExerciseDefinitions.findIndex(ex => ex == id)
        if (ind > -1) {
          if (this.editedExercise && this.participantsExercises) {
            this.selectedExerciseDefinitionsData[ind].selected = !this
              .selectedExerciseDefinitionsData[ind].selected
          } else {
            this.selectedExerciseDefinitions.splice(ind, 1)
            this.selectedExerciseDefinitionsData.splice(ind, 1)
          }
        } else {
          this.selectedExerciseDefinitions.push(id)
          this.selectedExerciseDefinitionsData.push({
            ...exObj,
            selected: true,
          })
        }
        this.calculateExerciseDefinitions()
      }
    },
    reorderSelectedExerciseDefinitions() {
      this.selectedExerciseDefinitions = this.selectedExerciseDefinitionsData.map(
        item => {
          return item.id
        },
      )
    },
    calculateExerciseDefinitions() {
      if (!this.readonly)
        this.searchedExerciseDefinitions = [...this.exerciseDefinitions]
      else {
        if (this.editedExercise?.type == 'SERIES') {
          let key = 'exercises'
          if (this.editedExercise.exerciseDefinitions)
            key = 'exerciseDefinitions'
          this.searchedExerciseDefinitions = [...this.editedExercise[key]]
        }
      }

      this.searchedExerciseDefinitions.forEach((item, index) => {
        if (this.selectedExerciseDefinitions.includes(item.id)) {
          this.searchedExerciseDefinitions[index] = Object.assign(
            {},
            this.searchedExerciseDefinitions[index],
            { selected: true },
          )
        } else this.searchedExerciseDefinitions[index]['selected'] = false
      })
      this.formIsValid()
    },
    setupExerciseData() {
      if (
        !this.newExerciseData.media &&
        !this.newExerciseData.currentMedia &&
        this.exerciseResource?.id
      ) {
        this.newExerciseData.currentMedia = {
          ...this.exerciseResource,
          mlSourceResourceId: this.exerciseResource.id,
        }
        this.newExerciseData.media = {
          ...this.exerciseResource,
          mlSourceResourceId: this.exerciseResource.id,
        }
      }

      if (
        !this.newExerciseData.additionalMedia &&
        !this.newExerciseData.currentAdditionalMedia &&
        this.exerciseResourceAdditional?.id
      ) {
        this.newExerciseData.currentAdditionalMedia = {
          ...this.exerciseResourceAdditional,
          mlSourceResourceId: this.exerciseResourceAdditional.id,
        }
        this.newExerciseData.additionalMedia = {
          ...this.exerciseResourceAdditional,
          mlSourceResourceId: this.exerciseResourceAdditional.id,
        }
      }

      if (this.editedExercise) {
        this.newExerciseData = { ...this.editedExercise }

        if (!this.newExerciseData.mobileStaticMedia) {
          this.newExerciseData.mobileStaticMedia = null
          this.newExerciseData.currentAdditionalMedia = null
        } else {
          this.newExerciseData.currentMobileStaticMedia = this.newExerciseData
            .mobileStaticMedia?.mediaUrl
            ? {
                thumbnailUrl: this.newExerciseData.mobileStaticMedia?.mediaUrl,
                mediaUrl: this.newExerciseData.mobileStaticMedia?.mediaUrl,
              }
            : null
        }

        if (!this.newExerciseData.media) {
          this.newExerciseData.media = null
          this.newExerciseData.currentMedia = null
        } else {
          this.newExerciseData.currentMedia =
            this.newExerciseData?.media?.mediaUrl ||
            this.newExerciseData?.media?.fileUrl ||
            this.newExerciseData?.media?.previewUrl ||
            this.newExerciseData?.media?.thumbnailUrl
              ? {
                  fileTypeId: this.newExerciseData?.media?.fileTypeId,
                  mediaUrl:
                    this.newExerciseData?.media?.mediaUrl ||
                    this.newExerciseData?.media?.fileUrl ||
                    this.newExerciseData?.media?.previewUrl ||
                    null,
                  thumbnailUrl: this.newExerciseData?.media?.thumbnailUrl,
                }
              : null
        }

        if (!this.newExerciseData.additionalMedia) {
          this.newExerciseData.additionalMedia = null
          this.newExerciseData.currentAdditionalMedia = null
        } else {
          this.newExerciseData.currentAdditionalMedia = this.newExerciseData
            ?.additionalMedia?.thumbnailUrl
            ? {
                mediaUrl:
                  this.newExerciseData?.additionalMedia?.mediaUrl ||
                  this.newExerciseData?.additionalMedia?.fileUrl ||
                  this.newExerciseData?.additionalMedia?.previewUrl ||
                  null,
                thumbnailUrl: this.newExerciseData?.additionalMedia
                  ?.thumbnailUrl,
              }
            : null
        }

        if (this.repetitionOptionsEnabled) {
          if (!this.newExerciseData.repetitionWeeks)
            this.newExerciseData.repetitionWeeks = 0
          if (!this.newExerciseData.repetitionDays)
            this.newExerciseData.repetitionDays = []

          this.repetitionWeeksValue = this.newExerciseData.repetitionWeeks
          this.repetitionDaysValue = this.newExerciseData.repetitionDays
        }

        if (
          this.editedExercise.restTimeAfterExercise ||
          this.editedExercise.restTimeAfterExercise === 0
        )
          this.restExercisesValue = this.editedExercise.restTimeAfterExercise
        if (this.editedExercise.circuits)
          this.circuitsValue = this.editedExercise.circuits
        if (this.editedExercise.exerciseDefinitions?.length > 0) {
          let ids = []
          this.editedExercise.exerciseDefinitions.forEach(exercise => {
            ids.push(exercise.id)
          })
          this.selectedExerciseDefinitions = ids
          this.selectedExerciseDefinitionsData = this.editedExercise.exerciseDefinitions.map(
            item => {
              return { ...item, ...{ selected: true } }
            },
          )
        } else if (this.editedExercise.exercises?.length > 0) {
          let ids = []
          this.editedExercise.exercises.forEach(exercise => {
            ids.push(exercise.id)
          })
          this.selectedExerciseDefinitions = ids
          this.selectedExerciseDefinitionsData = this.editedExercise.exercises.map(
            item => {
              return { ...item, ...{ selected: true } }
            },
          )
        }

        this.calculateExerciseDefinitions()
        this.formIsValid()
      }
    },
    prevStepSeries() {
      if (this.tabSeries == 0 && this.cancelable) this.$emit('cancel')
      else {
        this.tabSeries--
      }
    },
    cancelRemoval() {
      let ids = []
      let key = 'exercises'
      if (this.editedExercise.exerciseDefinitions) key = 'exerciseDefinitions'
      this.editedExercise[key].forEach(exercise => {
        ids.push(exercise.id)
      })
      this.selectedExerciseDefinitions = ids
      this.selectedExerciseDefinitionsData = this.editedExercise[key].map(
        item => {
          return { ...item, ...{ selected: true } }
        },
      )
      this.showConfirmationPopup = false
    },
    onClickOutside() {
      this.cancelRemoval()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.cancelRemoval()
    },
    getData(data) {
      data.page ? data.page-- : (data.page = 0)
      this.defaultQueryParams = { ...this.defaultQueryParams, ...data }
      this.$emit('on-series-search', this.defaultQueryParams)
    },
    additionalStepAction(val) {
      if (this.dontShowAgain) this.disableCircuitsAdditionalStep()
      if (val == 'yes') {
        this.additionalStep = false
      } else {
        this.closeDialog()
      }
    },
    disableCircuitsAdditionalStep() {
      if (!this.user.settings) {
        const settings = {
          routines_circuit_disable_tutorial: 1,
        }
        this.user.settings = JSON.stringify(settings)
      } else {
        const settings = { ...this.getSettings }
        settings.routines_circuit_disable_tutorial = 1
        this.user.settings = JSON.stringify(settings)
      }
      this.updateProfile({
        profileId: this.getProfileId,
        data: { ...this.user },
      })
    },
    setMedia(thumbnail = null) {
      this.setExerciseFile('media', thumbnail)
    },
    setAdditionalMedia(thumbnail = null) {
      this.setExerciseFile('additionalMedia', thumbnail)
    },
    setExerciseFile(exerciseFileType, thumbnail = null) {
      const availableTypes = ['media', 'additionalMedia']

      if (!availableTypes.includes(exerciseFileType)) {
        return
      }

      if (thumbnail === null) {
        // remove
        this.newExerciseData[exerciseFileType] = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.newExerciseData[exerciseFileType] = {
            mlSourceResourceId: file.id,
          }
        } else if (type === 'file-picker') {
          this.newExerciseData[exerciseFileType] = file
        } else if (type === 'mobile-static-media') {
          this.newExerciseData.mobileStaticMedia = file
          this.newExerciseData[exerciseFileType] = { remove: true }
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.exercise-definition-dialog {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &__toolbar {
    flex-grow: 0;
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &__tabs {
    flex-grow: 0;
  }
  &__tabs-items {
    background-color: $bh-blue--4;
    flex-grow: 1;
    & > .v-window__container {
      height: 100%;
      flex-grow: 1;
    }
  }
  &__tab {
    height: 100%;
    background-color: $bh-blue--4;
    display: flex;
    flex-direction: column;
    & > .v-tabs-items {
      overflow: auto;
      flex-grow: 1;
      background-color: $bh-blue--4;
    }
    .v-input--switch {
      margin: 0;
    }
    .v-label {
      font-size: 12px;
      color: $bh-gray--2;
    }
  }
  &__dots {
    span {
      width: 16px;
      height: 16px;
      display: inline-block;
      border-radius: 50%;
      background-color: $bh-white;
      margin: 0 3px;
      transition-duration: 300ms;
      &.active {
        background-color: $bh-blue;
      }
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1 * $gutter;
      padding-right: 1 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-button {
    margin-right: 10px;
    .v-btn {
      cursor: pointer;
    }
    & > label {
      cursor: pointer;
    }
    label {
    }
    .v-btn::before {
      display: none;
    }
    .v-icon {
      &:first-child {
        padding-right: 10px;
      }
      &:last-child {
        padding-left: 10px;
      }
    }
  }
  &-file-details {
    display: inline-block;
    vertical-align: middle;
    flex-grow: 1;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-right: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-right: 10px;
    }
    .v-icon {
      margin-left: 5px;
      padding: 4px;
    }
    span {
      cursor: default;
      white-space: nowrap;
      max-width: calc(100% - 80px);
      display: inline-block;
      vertical-align: middle;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }

  &__repetition-day {
    font-weight: 600;
    background-color: $bh-white !important;
    color: $bh-blue !important;
    border: 1px solid $bh-blue !important;

    &--active {
      background-color: $bh-blue !important;
      color: $bh-white !important;
    }
  }
}

.add-media-menu__list-item {
  .v-btn {
    width: 100%;
    justify-content: flex-start;

    .v-icon {
      width: 16px;
    }
  }

  .file-uploads {
    vertical-align: middle;
    overflow: auto !important;
    width: 100%;

    .v-btn {
      width: 100% !important;
    }

    &.file-uploads-html5 {
      &:not(.disabled) label:hover {
        opacity: 0.08;
      }
      &:not(.disabled) label {
        cursor: pointer;
      }
      label {
        background-color: $bh-blue !important;
        transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
        pointer-events: none;
        opacity: 0;
      }
    }
  }
}

.exercise-definition-dialog-form-body {
  .file-uploads {
    vertical-align: middle;
    overflow: auto !important;
    &.disabled {
      pointer-events: none;
      .v-btn {
        background-color: rgba(0, 0, 0, 0.12) !important;
        color: rgba(0, 0, 0, 0.26) !important;
      }
    }
  }
}
</style>
